import { ref } from "vue";
import { useTimeoutFn } from "@vueuse/core";
import { useWpsClient } from "@/wps/client";
import { WordDefinition } from "../types";
import { FrameClickEvent } from "@readium/navigator-html-injectables";
import { rangeFromPoint } from "@/../modules/dom-utils/text/click";
import { getDebugMode } from "@/helpers/helperFunctions";
import {
  expandRangeToWord,
  retrieveAfter,
  retrieveBefore,
} from "@/../modules/dom-utils/text/range";
import { BASE } from "@/consts";
import { distance } from "../../../../modules/shared-utils/math";

const activeDefinition = ref<WordDefinition | null>(null);
export function useDictionary() {
  const wpsClient = useWpsClient();

  async function handleClick(
    event: FrameClickEvent,
    currentIframe: HTMLIFrameElement
  ): Promise<void> {
    useTimeoutFn(async () => {
      if (
        currentIframe &&
        !wpsClient.media.isMediaOverlay &&
        currentIframe.contentWindow?.getSelection()?.toString() === ""
      ) {
        return await handleClickOnText(event, currentIframe);
      }
    }, 150);
  }

  async function handleClickOnText(
    e: FrameClickEvent,
    iframe: HTMLIFrameElement
  ) {
    const { word, coordinates, context, distance } = getWordBoundaries(
      e,
      iframe
    );

    if (getDebugMode())
      console.log("word boundaries: ", word, coordinates, distance, context);
    if (!word || distance > 20) return;
    // Get iframe position relative to the viewport
    const iframeRect = iframe.getBoundingClientRect();

    const response = await fetch(`${BASE}/dictionary/api/definitions/${word}`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ context }),
    });

    if (!response.ok) {
      console.error("Failed to fetch definition");
      return;
    }

    const definitions = await response.json();

    if (!definitions?.length) {
      if (getDebugMode()) console.log("no definition found for", word);
      return;
    }

    // Mocked definition data -- will be replaced with actual dictionary API call
    activeDefinition.value = {
      identity: word,
      details: definitions.map((def) => {
        return {
          title: def.word,
          type: def.synt,
          image: def.image ? `${BASE}/dictionary${def.image}` : null,
          description: def.definition,
          synonyms: def.synonyms?.length ? def.synonyms : undefined,
        };
      }),
      coordinates: {
        x: coordinates.x + iframeRect.left,
        y: coordinates.y + iframeRect.top,
        width: coordinates.width,
        height: coordinates.height,
      },
      context,
      clear() {
        activeDefinition.value = null;
      },
    };
    if (getDebugMode()) console.log("definition", activeDefinition.value);
  }

  function getWordBoundaries(
    event: FrameClickEvent,
    iframe: HTMLIFrameElement
  ): {
    word: string | null;
    coordinates: { x: number; y: number; width: number; height: number };
    context: { word: string; before: string; after: string };
    distance: number;
  } | null {
    const doc = iframe.contentDocument;
    const clickedRange = rangeFromPoint(
      { clientX: event.x, clientY: event.y },
      doc
    );
    const wordRange = expandRangeToWord(clickedRange);

    const before = retrieveBefore(wordRange, 30);
    const after = retrieveAfter(wordRange, 30);

    const coordinates = wordRange.getClientRects().item(0);

    const word = wordRange.toString();
    return {
      word,
      coordinates,
      context: { word, before, after },
      distance: distance(event, coordinates),
    };
  }
  return { handleClick, activeDefinition };
}
